import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/config";
import { toast } from "sonner";
import { Upload, Trash2, Star, EyeOff, Eye, Loader2, ImagePlus } from "lucide-react";

const MAX_EDGE = 1920;

async function shrinkImage(file) {
  if (!file.type.startsWith("image/") || file.type === "image/gif") return file;
  const bitmap = await createImageBitmap(file).catch(() => null);
  if (!bitmap) return file;
  const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
  if (scale === 1 && file.size < 2.5 * 1024 * 1024) return file;
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  canvas.getContext("2d").drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.86));
  return blob ? new File([blob], file.name.replace(/\.[^.]+$/, "") + ".jpg", { type: "image/jpeg" }) : file;
}

export default function PhotoManager({ secret }) {
  const headers = { "X-Admin-Secret": secret };
  const [tours, setTours] = useState([]);
  const [slug, setSlug] = useState("");
  const [data, setData] = useState(null);
  const [caption, setCaption] = useState("");
  const [asHero, setAsHero] = useState(false);
  const [uploading, setUploading] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/tours").then((r) => {
      const list = r.data.tours || [];
      setTours(list);
      if (list.length && !slug) setSlug(list[0].slug);
    });
    // eslint-disable-next-line
  }, []);

  const load = async (s) => {
    if (!s) return;
    const r = await api.get(`/admin/tours/${s}/photos`, { headers });
    setData(r.data);
  };
  useEffect(() => { load(slug); /* eslint-disable-next-line */ }, [slug]);

  const upload = async (files) => {
    if (!files.length) return;
    let done = 0;
    for (const raw of Array.from(files)) {
      setUploading(`Uploading ${done + 1} of ${files.length}…`);
      try {
        const file = await shrinkImage(raw);
        const fd = new FormData();
        fd.append("file", file);
        fd.append("caption", caption);
        fd.append("is_hero", asHero && done === 0 ? "true" : "false");
        await api.post(`/admin/tours/${slug}/photos`, fd, { headers });
        done += 1;
      } catch (e) {
        toast.error(e?.response?.data?.detail || `Failed to upload ${raw.name}`);
      }
    }
    setUploading(null);
    setCaption("");
    setAsHero(false);
    if (fileRef.current) fileRef.current.value = "";
    if (done) toast.success(`${done} photo${done > 1 ? "s" : ""} added`);
    load(slug);
  };

  const patch = async (id, body) => {
    await api.patch(`/admin/photos/${id}`, body, { headers });
    load(slug);
  };
  const remove = async (id) => {
    if (!window.confirm("Remove this photo from the tour?")) return;
    await api.delete(`/admin/photos/${id}`, { headers });
    toast.success("Photo removed");
    load(slug);
  };
  const toggleStock = async (url, hidden) => {
    await api.post(`/admin/tours/${slug}/hide-stock`, { url, hidden }, { headers });
    load(slug);
  };

  const tour = tours.find((t) => t.slug === slug);

  return (
    <div data-testid="admin-photo-manager" className="mt-8 grid lg:grid-cols-12 gap-8">
      <div className="lg:col-span-4 space-y-6">
        <div className="bg-[#FFFFFF] border border-[#E2D5C3] rounded-3xl shadow-[0_4px_20px_-2px_rgba(35,23,14,0.06)] p-6">
          <label className="overline">Tour</label>
          <select data-testid="photo-tour-select" value={slug} onChange={(e) => setSlug(e.target.value)} className="mt-3 w-full bg-[#FAF6F0] border border-[#BCA993] rounded-full px-5 py-3 focus:outline-none focus:border-[#A6521B]">
            {tours.map((t) => <option key={t.slug} value={t.slug}>{t.title} · {t.duration_label}</option>)}
          </select>
          {tour && <p className="text-xs text-[#23170E]/55 mt-3">{tour.park}</p>}
        </div>

        <div
          className="bg-[#FFFFFF] border border-[#E2D5C3] rounded-3xl shadow-[0_4px_20px_-2px_rgba(35,23,14,0.06)] p-6"
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); upload(e.dataTransfer.files); }}
        >
          <p className="overline">Add photos</p>
          <label className="overline block mt-5">Caption (optional)</label>
          <input data-testid="photo-caption-input" value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="e.g. Elephants at the Tarangire River" className="mt-2 w-full bg-[#FAF6F0] border border-[#BCA993] rounded-full px-5 py-3 text-sm focus:outline-none focus:border-[#A6521B]" />
          <label className="mt-4 flex items-center gap-3 text-sm text-[#23170E]/80 cursor-pointer">
            <input data-testid="photo-hero-checkbox" type="checkbox" checked={asHero} onChange={(e) => setAsHero(e.target.checked)} className="accent-[#A6521B]" />
            Use as the tour's cover photo
          </label>
          <input ref={fileRef} data-testid="photo-file-input" type="file" accept="image/jpeg,image/png,image/webp,image/gif" multiple className="hidden" onChange={(e) => upload(e.target.files)} />
          <button
            data-testid="photo-upload-btn"
            type="button"
            disabled={!!uploading || !slug}
            onClick={() => fileRef.current?.click()}
            className="mt-6 w-full btn-pill btn-pill-primary justify-center disabled:opacity-60"
          >
            {uploading ? <><Loader2 size={14} className="animate-spin" /> {uploading}</> : <><Upload size={14} /> Choose photos</>}
          </button>
          <p className="text-xs text-[#23170E]/50 mt-3 text-center">or drag & drop here · JPG, PNG, WEBP · resized automatically</p>
        </div>
      </div>

      <div className="lg:col-span-8 space-y-10">
        <section>
          <div className="flex items-baseline justify-between">
            <h3 className="font-marcellus text-2xl">Your photos</h3>
            <span className="text-xs text-[#23170E]/55">{data?.photos?.length || 0} uploaded</span>
          </div>
          {data && data.photos.length === 0 && (
            <div data-testid="photo-empty-state" className="mt-4 border border-dashed border-[#23170E]/20 rounded-3xl p-12 text-center text-[#23170E]/55">
              <ImagePlus className="mx-auto mb-3" size={28} />
              No photos yet for this tour. Upload some on the left — they'll appear first in the gallery.
            </div>
          )}
          <div className="mt-4 grid sm:grid-cols-2 md:grid-cols-3 gap-4">
            {data?.photos?.map((p) => (
              <div key={p.id} data-testid={`photo-item-${p.id}`} className="bg-[#FFFFFF] border border-[#E2D5C3] rounded-2xl shadow-[0_4px_20px_-2px_rgba(35,23,14,0.06)] overflow-hidden">
                <div className="relative aspect-[4/3]">
                  <img src={`/api/media/${p.id}`} alt={p.caption || p.original_filename} className="w-full h-full object-cover" loading="lazy" />
                  {p.is_hero && <span className="absolute top-2 left-2 bg-[#A6521B] text-[#FAF6F0] text-[10px] uppercase tracking-[0.15em] rounded-full px-2.5 py-1 flex items-center gap-1"><Star size={10} /> Cover</span>}
                </div>
                <div className="p-3 space-y-2">
                  <input
                    data-testid={`photo-caption-${p.id}`}
                    defaultValue={p.caption}
                    placeholder="Add a caption"
                    onBlur={(e) => e.target.value !== p.caption && patch(p.id, { caption: e.target.value })}
                    className="w-full bg-transparent border-b border-[#BCA993] text-sm py-1 focus:outline-none focus:border-[#A6521B]"
                  />
                  <div className="flex justify-between text-xs">
                    <button data-testid={`photo-set-hero-${p.id}`} disabled={p.is_hero} onClick={() => patch(p.id, { is_hero: true })} className="text-[#A6521B] disabled:opacity-40 flex items-center gap-1"><Star size={12} /> Set as cover</button>
                    <button data-testid={`photo-delete-${p.id}`} onClick={() => remove(p.id)} className="text-[#A6352B] flex items-center gap-1"><Trash2 size={12} /> Remove</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {data && (
          <section>
            <h3 className="font-marcellus text-2xl">Stock photos</h3>
            <p className="text-xs text-[#23170E]/55 mt-1">Hide the placeholder images once your own photos are in place.</p>
            <div className="mt-4 grid sm:grid-cols-2 md:grid-cols-3 gap-4">
              {data.stock_gallery.map((g) => {
                const hidden = data.hidden_urls.includes(g.url);
                return (
                  <div key={g.url} className={`bg-[#FFFFFF] border border-[#E2D5C3] rounded-2xl shadow-[0_4px_20px_-2px_rgba(35,23,14,0.06)] overflow-hidden ${hidden ? "opacity-50" : ""}`}>
                    <div className="aspect-[4/3]"><img src={g.url} alt={g.caption} className="w-full h-full object-cover" loading="lazy" /></div>
                    <div className="p-3 flex items-center justify-between gap-2">
                      <span className="text-xs text-[#23170E]/70 truncate">{g.caption}</span>
                      <button data-testid={`stock-toggle-${data.stock_gallery.indexOf(g)}`} onClick={() => toggleStock(g.url, !hidden)} className="text-xs text-[#A6521B] flex items-center gap-1 flex-shrink-0">
                        {hidden ? <><Eye size={12} /> Show</> : <><EyeOff size={12} /> Hide</>}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
